import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError } from '../api'
import {
  Badge,
  Empty,
  Field,
  FormGrid,
  Modal,
  Notice,
  Page,
  Panel,
  StatTile,
  Table,
  TileRow,
  fmtDateTime,
  fmtDuration,
  fmtNumber,
  fmtPhone,
  localTime,
} from '../components/ui'
import type {
  LeadExplanation,
  CampaignSummary,
  ImportPreview,
  ImportReport,
  LeadDetail,
  LeadPage,
  ListSummary,
} from '../types'

const STATUSES = [
  'new',
  'queued',
  'in_progress',
  'contacted',
  'callback',
  'exhausted',
  'dnc',
  'invalid',
  'suppressed',
]

export default function Leads() {
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([])
  const [lists, setLists] = useState<ListSummary[]>([])
  const [campaignId, setCampaignId] = useState('')
  const [listId, setListId] = useState('')
  const [status, setStatus] = useState('')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(0)
  const [data, setData] = useState<LeadPage | null>(null)
  const [loading, setLoading] = useState(true)
  const [openLead, setOpenLead] = useState<string | null>(null)
  const [importing, setImporting] = useState(false)
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    api.campaigns().then(setCampaigns).catch(() => setCampaigns([]))
  }, [])

  useEffect(() => {
    if (!campaignId) {
      setLists([])
      setListId('')
      return
    }
    api.lists(campaignId).then(setLists).catch(() => setLists([]))
  }, [campaignId])

  const load = useCallback(() => {
    setLoading(true)
    api
      .leads({
        campaign_id: campaignId || undefined,
        list_id: listId || undefined,
        status: status || undefined,
        q: q || undefined,
        page,
      })
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [campaignId, listId, status, q, page])

  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0)
    return () => clearTimeout(t)
  }, [load, q])

  const total = data?.total ?? 0
  const perPage = data?.per_page ?? 50
  const pages = Math.max(1, Math.ceil(total / perPage))

  return (
    <Page
      title="Leads"
      subtitle="Load, search and manage the data you dial"
      actions={
        <button className="primary" onClick={() => setImporting(true)}>
          Import CSV
        </button>
      }
    >
      {notice && (
        <Notice kind={notice.kind} onDismiss={() => setNotice(null)}>
          {notice.text}
        </Notice>
      )}

      <TileRow>
        <StatTile label="Matching leads" value={fmtNumber(total)} />
        <StatTile
          label="Lists"
          value={campaignId ? lists.length : campaigns.reduce((a, c) => a + c.lists, 0)}
        />
        <StatTile
          label="Dialable"
          value={fmtNumber(
            campaignId
              ? lists.reduce((a, l) => a + l.dialable, 0)
              : campaigns.reduce((a, c) => a + c.dialable, 0),
          )}
          tone="live"
        />
      </TileRow>

      <Panel>
        <div className="filter-bar">
          <select value={campaignId} onChange={(e) => { setCampaignId(e.target.value); setPage(0) }}>
            <option value="">All campaigns</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <select
            value={listId}
            onChange={(e) => { setListId(e.target.value); setPage(0) }}
            disabled={!campaignId}
          >
            <option value="">All lists</option>
            {lists.map((l) => (
              <option key={l.id} value={l.id}>{l.name} ({fmtNumber(l.total)})</option>
            ))}
          </select>

          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(0) }}>
            <option value="">Any status</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s.replace('_', ' ')}</option>
            ))}
          </select>

          <input
            className="grow"
            placeholder="Search by name or number"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0) }}
          />
        </div>

        {loading && !data ? (
          <p className="muted">Loading…</p>
        ) : !data || data.leads.length === 0 ? (
          <Empty
            message="No leads match."
            hint={
              total === 0 && !q
                ? 'Import a CSV to get started — the button is at the top right.'
                : 'Try widening the filters.'
            }
          />
        ) : (
          <>
            <Table head={['Number', 'Name', 'Location', 'Their time', 'List', 'Status', 'Attempts', 'Last try']}>
              {data.leads.map((l) => (
                <tr key={l.id} className="clickable" onClick={() => setOpenLead(l.id)}>
                  <td className="mono strong">{fmtPhone(l.phone_e164)}</td>
                  <td>{[l.first_name, l.last_name].filter(Boolean).join(' ') || <span className="muted">—</span>}</td>
                  <td className="muted">
                    {[l.city, l.state].filter(Boolean).join(', ') || '—'}
                  </td>
                  <td className="mono muted">{localTime(l.timezone) ?? '—'}</td>
                  <td className="muted">{l.list_name}</td>
                  <td><LeadStatus status={l.status} /></td>
                  <td>{l.attempt_count}</td>
                  <td className="muted">{fmtDateTime(l.last_attempt_at)}</td>
                </tr>
              ))}
            </Table>

            <div className="pager">
              <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>← Previous</button>
              <span className="muted">
                Page {page + 1} of {fmtNumber(pages)} · {fmtNumber(total)} leads
              </span>
              <button disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>Next →</button>
            </div>
          </>
        )}
      </Panel>

      {openLead && <LeadDrawer id={openLead} onClose={() => { setOpenLead(null); load() }} />}

      {importing && (
        <ImportWizard
          campaigns={campaigns}
          onClose={() => setImporting(false)}
          onDone={(report) => {
            setImporting(false)
            setNotice({
              kind: 'success',
              text: `Imported ${fmtNumber(report.imported)} leads. ${fmtNumber(report.duplicates)} duplicates skipped, ${fmtNumber(report.rejected)} rejected.`,
            })
            load()
          }}
        />
      )}
    </Page>
  )
}

function LeadStatus({ status }: { status: string }) {
  const tone =
    status === 'contacted' ? 'ok'
    : status === 'dnc' ? 'danger'
    : status === 'callback' ? 'live'
    : status === 'new' || status === 'queued' ? 'muted'
    : 'warn'
  return <Badge tone={tone as never}>{status.replace('_', ' ')}</Badge>
}

// ========================================================== lead drawer ====

function LeadDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const [data, setData] = useState<LeadDetail | null>(null)
  const [saving, setSaving] = useState(false)
  const [explaining, setExplaining] = useState(false)
  const [form, setForm] = useState<Record<string, string>>({})

  useEffect(() => {
    api.lead(id).then((d) => {
      setData(d)
      setForm({
        first_name: d.lead.first_name ?? '',
        last_name: d.lead.last_name ?? '',
        city: d.lead.city ?? '',
        state: d.lead.state ?? '',
        postal_code: d.lead.postal_code ?? '',
        email: d.lead.email ?? '',
      })
    })
  }, [id])

  const save = async () => {
    setSaving(true)
    try {
      await api.updateLead(id, form)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  if (!data) {
    return (
      <Modal title="Lead" onClose={onClose}>
        <p className="muted">Loading…</p>
      </Modal>
    )
  }

  const l = data.lead
  const tz = localTime(l.timezone)

  return (
    <Modal title={fmtPhone(l.phone_e164)} onClose={onClose} wide>
      {data.dnc.length > 0 && (
        <Notice kind="error">
          This number is suppressed ({data.dnc.join(', ')}). Calls to it will be refused.
        </Notice>
      )}

      <div className="lead-meta">
        <span><b>Status</b> <LeadStatus status={l.status} /></span>
        <span><b>Attempts</b> {l.attempt_count}</span>
        <span><b>Last try</b> {fmtDateTime(l.last_attempt_at)}</span>
        {tz && <span><b>Their time</b> <span className="mono">{tz}</span></span>}
        {l.line_type && <span><b>Line</b> {l.line_type}</span>}
        <button
          className="chip"
          onClick={() => setExplaining(true)}
          title="Run the hopper rules and the compliance gate against this lead, read-only, and show every check."
        >
          Why isn't this being called?
        </button>
      </div>

      {explaining && <ExplainPanel leadId={id} />}

      <h3 className="form-section">Details</h3>
      <FormGrid>
        {(['first_name', 'last_name', 'city', 'state', 'postal_code', 'email'] as const).map((k) => (
          <Field key={k} label={k.replace('_', ' ')}>
            <input
              value={form[k] ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
            />
          </Field>
        ))}
      </FormGrid>

      {Object.keys(l.custom ?? {}).length > 0 && (
        <>
          <h3 className="form-section">Custom fields</h3>
          <dl className="kv">
            {Object.entries(l.custom).map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{String(v)}</dd>
              </div>
            ))}
          </dl>
        </>
      )}

      <h3 className="form-section">Call history</h3>
      {data.history.length === 0 ? (
        <Empty message="This lead has never been dialed." />
      ) : (
        <Table head={['When', 'Agent', 'Answered', 'Talk', 'Disposition', 'Note']}>
          {data.history.map((h) => (
            <tr key={h.call_id}>
              <td className="muted">{fmtDateTime(h.at)}</td>
              <td>{h.agent ?? '—'}</td>
              <td>{h.answered ? 'Yes' : 'No'}</td>
              <td className="mono">{fmtDuration(h.talk_sec)}</td>
              <td>{h.disposition_label ?? <span className="muted">—</span>}</td>
              <td className="muted">{h.note ?? ''}</td>
            </tr>
          ))}
        </Table>
      )}

      {data.callbacks.length > 0 && (
        <>
          <h3 className="form-section">Callbacks</h3>
          <Table head={['Scheduled', 'Status', 'Note']}>
            {data.callbacks.map((c) => (
              <tr key={c.id}>
                <td>{fmtDateTime(c.scheduled_at)}</td>
                <td><Badge>{c.status}</Badge></td>
                <td className="muted">{c.note ?? ''}</td>
              </tr>
            ))}
          </Table>
        </>
      )}

      <div className="modal-actions">
        <button className="ghost" onClick={onClose}>Close</button>
        <button className="primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </Modal>
  )
}

// ======================================================== import wizard ====

type Step = 'choose' | 'map' | 'running' | 'done'

const FIELDS: { key: string; label: string; required?: boolean }[] = [
  { key: 'phone', label: 'Phone number', required: true },
  { key: 'first_name', label: 'First name' },
  { key: 'last_name', label: 'Last name' },
  { key: 'address1', label: 'Address' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'postal_code', label: 'ZIP / postal' },
  { key: 'email', label: 'Email' },
  { key: 'alt_phone', label: 'Alternate phone' },
]

function ImportWizard({
  campaigns,
  onClose,
  onDone,
}: {
  campaigns: CampaignSummary[]
  onClose: () => void
  onDone: (r: ImportReport) => void
}) {
  const [step, setStep] = useState<Step>('choose')
  const [campaignId, setCampaignId] = useState('')
  const [lists, setLists] = useState<ListSummary[]>([])
  const [listId, setListId] = useState('')
  const [newListName, setNewListName] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [mapping, setMapping] = useState<Record<string, number | null>>({})
  const [customCols, setCustomCols] = useState<number[]>([])
  const [hasHeader, setHasHeader] = useState(true)
  const [dedupe, setDedupe] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [report, setReport] = useState<ImportReport | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!campaignId) return
    api.lists(campaignId).then(setLists).catch(() => setLists([]))
  }, [campaignId])

  const pickFile = async (f: File) => {
    setFile(f)
    setError(null)
    try {
      const p = await api.importPreview(f)
      setPreview(p)
      const m: Record<string, number | null> = {}
      for (const field of FIELDS) m[field.key] = p.guessed_mapping[field.key] ?? null
      setMapping(m)
      // Columns the guesser did not claim become custom fields by default;
      // customers put useful things in them and losing that silently is worse
      // than carrying a few extra keys.
      const claimed = new Set(Object.values(p.guessed_mapping))
      setCustomCols(p.headers.map((_, i) => i).filter((i) => !claimed.has(i)))
      setStep('map')
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not read that file')
    }
  }

  const run = async () => {
    if (!file) return
    setError(null)
    setStep('running')
    try {
      let targetList = listId
      if (!targetList && newListName.trim()) {
        const created = await api.createList(campaignId, newListName.trim())
        targetList = created.id
      }
      if (!targetList) {
        setError('Choose a list, or name a new one.')
        setStep('map')
        return
      }

      const r = await api.importLeads(targetList, file, {
        phone: mapping.phone ?? 0,
        first_name: mapping.first_name,
        last_name: mapping.last_name,
        address1: mapping.address1,
        city: mapping.city,
        state: mapping.state,
        postal_code: mapping.postal_code,
        email: mapping.email,
        alt_phone: mapping.alt_phone,
        custom: customCols,
        has_header: hasHeader,
        dedupe,
      })
      setReport(r)
      setStep('done')
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Import failed')
      setStep('map')
    }
  }

  return (
    <Modal title="Import leads" onClose={onClose} wide>
      {error && <Notice kind="error" onDismiss={() => setError(null)}>{error}</Notice>}

      <ol className="steps">
        <li className={step === 'choose' ? 'on' : 'done'}>Choose file</li>
        <li className={step === 'map' ? 'on' : step === 'choose' ? '' : 'done'}>Map columns</li>
        <li className={step === 'done' ? 'on' : ''}>Import</li>
      </ol>

      {step === 'choose' && (
        <>
          <FormGrid>
            <Field label="Campaign">
              <select value={campaignId} onChange={(e) => setCampaignId(e.target.value)}>
                <option value="">— choose —</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </Field>
            <Field label="List" hint="Or leave blank and name a new one below.">
              <select value={listId} onChange={(e) => setListId(e.target.value)} disabled={!campaignId}>
                <option value="">— new list —</option>
                {lists.map((l) => (
                  <option key={l.id} value={l.id}>{l.name} ({fmtNumber(l.total)})</option>
                ))}
              </select>
            </Field>
            {!listId && (
              <Field label="New list name">
                <input
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="e.g. March buy — Florida"
                  disabled={!campaignId}
                />
              </Field>
            )}
          </FormGrid>

          <div
            className="dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault()
              const f = e.dataTransfer.files[0]
              if (f) pickFile(f)
            }}
            onClick={() => fileInput.current?.click()}
          >
            <input
              ref={fileInput}
              type="file"
              accept=".csv,text/csv,text/plain"
              hidden
              onChange={(e) => e.target.files?.[0] && pickFile(e.target.files[0])}
            />
            <strong>Drop a CSV here, or click to choose</strong>
            <span className="muted small">
              Any column order. Numbers in any format — they are normalised on import.
            </span>
          </div>

          {!campaignId && <p className="muted small">Choose a campaign first.</p>}
        </>
      )}

      {step === 'map' && preview && (
        <>
          <p className="muted small">
            {file?.name} · {preview.column_count} columns. Check the mapping below — the phone
            column is the only one that is required.
          </p>

          <div className="mapping-grid">
            {FIELDS.map((f) => (
              <div key={f.key} className="mapping-row">
                <span className="mapping-label">
                  {f.label}
                  {f.required && <b className="req"> *</b>}
                </span>
                <select
                  value={mapping[f.key] ?? ''}
                  onChange={(e) =>
                    setMapping((m) => ({
                      ...m,
                      [f.key]: e.target.value === '' ? null : +e.target.value,
                    }))
                  }
                >
                  <option value="">— not in this file —</option>
                  {preview.headers.map((h, i) => (
                    <option key={i} value={i}>
                      {h || `Column ${i + 1}`}
                    </option>
                  ))}
                </select>
                <span className="mapping-sample muted">
                  {mapping[f.key] !== null && mapping[f.key] !== undefined
                    ? preview.sample[0]?.[mapping[f.key] as number] ?? ''
                    : ''}
                </span>
              </div>
            ))}
          </div>

          <h3 className="form-section">Keep as custom fields</h3>
          <div className="chip-row">
            {preview.headers.map((h, i) => (
              <button
                key={i}
                className={customCols.includes(i) ? 'chip on' : 'chip'}
                onClick={() =>
                  setCustomCols((c) => (c.includes(i) ? c.filter((x) => x !== i) : [...c, i]))
                }
              >
                {h || `Column ${i + 1}`}
              </button>
            ))}
          </div>

          <div className="check-row">
            <label>
              <input type="checkbox" checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} />
              First row is a header
            </label>
            <label>
              <input type="checkbox" checked={dedupe} onChange={(e) => setDedupe(e.target.checked)} />
              Skip numbers already in this campaign
            </label>
          </div>

          <details className="preview-table">
            <summary>Preview first rows</summary>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>{preview.headers.map((h, i) => <th key={i}>{h || `Col ${i + 1}`}</th>)}</tr>
                </thead>
                <tbody>
                  {preview.sample.map((row, i) => (
                    <tr key={i}>{row.map((c, j) => <td key={j}>{c}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>

          <div className="modal-actions">
            <button className="ghost" onClick={() => setStep('choose')}>Back</button>
            <button
              className="primary"
              onClick={run}
              disabled={mapping.phone === null || mapping.phone === undefined}
            >
              Import {file?.name}
            </button>
          </div>
        </>
      )}

      {step === 'running' && (
        <div className="running">
          <div className="spinner" />
          <p>Importing. Large files take a moment — normalising every number and resolving time zones.</p>
        </div>
      )}

      {step === 'done' && report && (
        <>
          <TileRow>
            <StatTile label="Imported" value={fmtNumber(report.imported)} tone="ok" />
            <StatTile label="Duplicates skipped" value={fmtNumber(report.duplicates)} />
            <StatTile
              label="Rejected"
              value={fmtNumber(report.rejected)}
              tone={report.rejected > 0 ? 'warn' : undefined}
            />
            <StatTile
              label="No time zone"
              value={fmtNumber(report.unresolved_timezone)}
              tone={report.unresolved_timezone > 0 ? 'warn' : undefined}
              sub="curfew cannot be checked"
            />
          </TileRow>

          {report.rejects.length > 0 && (
            <>
              <h3 className="form-section">
                Rejected rows
                {report.rejects_truncated && (
                  <span className="form-section-note">showing the first {report.rejects.length}</span>
                )}
              </h3>
              <div className="scroll-box">
                <Table head={['Line', 'Value', 'Reason']}>
                  {report.rejects.map((r, i) => (
                    <tr key={i}>
                      <td className="mono">{r.line}</td>
                      <td className="mono">{r.value || <span className="muted">empty</span>}</td>
                      <td className="muted">{r.reason}</td>
                    </tr>
                  ))}
                </Table>
              </div>
            </>
          )}

          {report.unresolved_timezone > 0 && (
            <Notice kind="warn">
              {fmtNumber(report.unresolved_timezone)} leads have no resolvable time zone. Curfew is
              measured in the called party&apos;s local time, so those will be held back rather than
              dialed on a guess.
            </Notice>
          )}

          <div className="modal-actions">
            <button className="primary" onClick={() => onDone(report)}>Done</button>
          </div>
        </>
      )}
    </Modal>
  )
}

/**
 * The lead explainer.
 *
 * Two layers can hold a lead back, and they need different fixes: the hopper
 * (scheduling and configuration — status, eligibility, list, campaign) and the
 * gate (compliance facts about the number). The server runs both — the gate
 * part through the same evaluate() a live dial uses, read-only — and this
 * renders each check as pass/fail with the reason.
 */
function ExplainPanel({ leadId }: { leadId: string }) {
  const [data, setData] = useState<LeadExplanation | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.explainLead(leadId).then(setData).catch((e) => setError(String(e)))
  }, [leadId])

  if (error) return <Notice kind="error">{error}</Notice>
  if (!data) return <p className="muted">Checking…</p>

  const verdict = !data.hopper_would_serve
    ? { tone: 'warn' as const, text: 'The dialler never sees this lead — a hopper rule holds it back.' }
    : data.gate == null
      ? { tone: 'warn' as const, text: 'Hopper checks pass; the gate was not evaluated.' }
      : data.gate.outcome === 'allow'
        ? { tone: 'success' as const, text: 'This lead is callable right now. If it is not being dialled, the campaign is stopped, paced down, or other leads are simply ahead of it in the queue.' }
        : data.gate.outcome === 'defer'
          ? { tone: 'warn' as const, text: `Callable, but not yet: held until ${data.gate.until ? fmtDateTime(data.gate.until) : 'later'} (${data.gate.reason_code}).` }
          : { tone: 'error' as const, text: `The compliance gate refuses this number: ${data.gate.detail ?? data.gate.reason_code}` }

  return (
    <div className="explain-lead">
      <Notice kind={verdict.tone}>{verdict.text}</Notice>

      <Table head={['Layer', 'Check', '', 'Detail']}>
        {data.hopper.map((h) => (
          <tr key={h.check}>
            <td className="muted small">hopper</td>
            <td className="mono small">{h.check}</td>
            <td>{h.ok ? <Badge tone="ok">pass</Badge> : <Badge tone="danger">holds it</Badge>}</td>
            <td className="muted small">{h.detail}</td>
          </tr>
        ))}
        {data.gate?.checks_run.map((c) => {
          // The gate stops at the first failing check, so every check listed
          // before a non-allow verdict passed, and the last one is the verdict.
          const isLast = c === data.gate!.checks_run[data.gate!.checks_run.length - 1]
          const failed = isLast && data.gate!.outcome !== 'allow'
          return (
            <tr key={c}>
              <td className="muted small">gate</td>
              <td className="mono small">{c}</td>
              <td>
                {failed ? (
                  <Badge tone={data.gate!.outcome === 'defer' ? 'warn' : 'danger'}>
                    {data.gate!.outcome}
                  </Badge>
                ) : (
                  <Badge tone="ok">pass</Badge>
                )}
              </td>
              <td className="muted small">{failed ? data.gate!.detail ?? data.gate!.reason_code : ''}</td>
            </tr>
          )
        })}
      </Table>

      <p className="muted small">
        The gate rows come from the same code that guards a live dial — run read-only, with
        nothing written to the decision log. A cache-miss on phone intelligence performs a real
        provider lookup, exactly as a live dial would.
      </p>
    </div>
  )
}
